import Animated, { FadeIn, FadeOut, SlideInDown, SlideOutDown } from 'react-native-reanimated';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Avatar } from '@/components/avatar';
import { GhostButton } from '@/components/buttons';
import type { Person, Role } from '@/game/types';
import { colors, radius, type } from '@/theme';

type Props = {
  person: Person;
  roles: Role[];
  onAssign: (role: Role) => void;
  onCancel: () => void;
};

/** "Where do you draft them?" — the moment the pick becomes permanent. */
export function RoleSheet({ person, roles, onAssign, onCancel }: Props) {
  const insets = useSafeAreaInsets();

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      <Animated.View entering={FadeIn.duration(180)} exiting={FadeOut.duration(140)} style={styles.scrimWrap}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      </Animated.View>

      <Animated.View
        entering={SlideInDown.duration(260)}
        exiting={SlideOutDown.duration(180)}
        style={[styles.sheet, { paddingBottom: insets.bottom + 16 }]}>
        <View style={styles.header}>
          <Avatar initials={person.initials} accent={colors.gold} size={62} />
          <View style={styles.headerCopy}>
            <Text style={styles.kicker}>WHERE DO YOU DRAFT</Text>
            <Text style={styles.name} numberOfLines={2}>
              {person.name}?
            </Text>
          </View>
        </View>

        <View style={styles.tags}>
          {person.tags.map((tag) => (
            <Text key={tag} style={styles.tag}>
              {tag}
            </Text>
          ))}
        </View>

        <View style={styles.grid}>
          {roles.map((role, index) => (
            <Animated.View
              key={role}
              entering={FadeIn.delay(80 + index * 35).duration(200)}
              style={styles.cell}>
              <Pressable
                onPress={() => onAssign(role)}
                style={({ pressed }) => [styles.roleButton, pressed && styles.roleButtonPressed]}>
                <Text style={styles.roleLabel}>{role}</Text>
              </Pressable>
            </Animated.View>
          ))}
        </View>

        <GhostButton label="Back" onPress={onCancel} />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrimWrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(6, 6, 7, 0.86)',
  },
  sheet: {
    marginTop: 'auto',
    backgroundColor: colors.card,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: colors.border,
    paddingHorizontal: 18,
    paddingTop: 22,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  headerCopy: {
    flex: 1,
    gap: 4,
  },
  kicker: {
    ...type.label,
    color: colors.dim,
  },
  name: {
    ...type.title,
    fontSize: 28,
    color: colors.text,
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    ...type.label,
    fontSize: 10,
    color: colors.dim,
    backgroundColor: colors.cardHi,
    borderRadius: radius.pill,
    paddingHorizontal: 11,
    paddingVertical: 6,
    overflow: 'hidden',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  cell: {
    flexGrow: 1,
    flexBasis: '45%',
  },
  roleButton: {
    backgroundColor: colors.cardHi,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: 22,
    alignItems: 'center',
  },
  roleButtonPressed: {
    backgroundColor: colors.gold,
    borderColor: colors.gold,
    transform: [{ scale: 0.98 }],
  },
  roleLabel: {
    ...type.title,
    fontSize: 19,
    color: colors.text,
  },
});
